import { Formik, FormikHelpers } from "formik";
import {
  DesignCollectionsDraftApi,
  DesignCollectionsDraftApiUserDesignCollectionUpdateDraftRequest,
  SharedserviceMelcoModelsDesignCollection,
  SharedserviceMelcoModelsDesignListItem,
  UseApiQueryKey,
  useAuthenticatedAPIConfig,
  useFormSubmit,
} from "melco-shared-logic";
import { QueryObserverResult, useQueryClient } from "react-query";
import { SubmitAction } from "../../header/SavePublishHeader";
import { DisplayDesignCollectionDetailForm } from "./DisplayDesignCollectionDetailForm";
import { Drawer as DrawerType } from "../../../hooks/drawer/useGenericDrawer";
import { DirtyFormPrompt } from "../../form/DirtyFormPrompt";
import { queryKeyForDesignCollection } from "../../../hooks/libraries/designCollections/useDesignCollection";
import { ALL_PUBLISHED_STATES } from "../../../helper/publishedState";

type DesignCollectionDetailFormProps = {
  designCollection: SharedserviceMelcoModelsDesignCollection;
  refetch: () => Promise<QueryObserverResult<SharedserviceMelcoModelsDesignCollection, unknown>
  >;
  referencesDrawer: DrawerType;
  queryKey: UseApiQueryKey;
};

type DesignCollectionDetailFormValueType = {
  id: string;
  name: string;
  design_list: SharedserviceMelcoModelsDesignListItem[];
  submitAction: SubmitAction;
};

export const designCollectionToInitialValues = (
  designCollection: SharedserviceMelcoModelsDesignCollection
) => {
  const { id, name, design_list } = designCollection;

  const initialValues: DesignCollectionDetailFormValueType = {
    id: id!,
    name: name ?? "",
    design_list: design_list ?? [],
    submitAction: "save",
  };

  return initialValues;
};

export const DesignCollectionDetailForm: React.FC<
  DesignCollectionDetailFormProps
> = ({ designCollection, refetch, referencesDrawer, queryKey }) => {
  const queryClient = useQueryClient();
  const authenticatedApiConfig = useAuthenticatedAPIConfig();
  const { id } = designCollection;

  const onSubmit = async (
    values: DesignCollectionDetailFormValueType,
    formikHelpers: FormikHelpers<DesignCollectionDetailFormValueType>
  ) => {
    const { submitAction } = values;

    if (submitAction === "publish") {
      await onPublish(values, formikHelpers, { isManuallyResettingForm: true });
    } else {
      await onSave(values, formikHelpers, { isManuallyResettingForm: true });
    }
  };

  const handleSaveAndPublish = async (
    values: DesignCollectionDetailFormValueType,
    formikHelpers: FormikHelpers<DesignCollectionDetailFormValueType>,
    options: {
      publish: boolean;
    }
  ) => {
    const { publish } = options;
    const { name, design_list } = values;

    const api = new DesignCollectionsDraftApi(authenticatedApiConfig());

    const entityAttributes: DesignCollectionsDraftApiUserDesignCollectionUpdateDraftRequest =
    {
      id: id!,
      sharedserviceMelcoModelsUpdateDesignCollection: {
        name,
        design_list,
      },
    };

    let newEntity = await api.userDesignCollectionUpdateDraft(entityAttributes);

    if (publish) {
      newEntity = await api.userDesignCollectionPublish({
        id: id!,
      });
    }

    // update ui with new data
    formikHelpers.resetForm({
      values: designCollectionToInitialValues(newEntity),
    });
    queryClient.setQueryData(queryKey, newEntity);

    return {
      successMessages: [
        {
          message: "default",
        },
      ],
    };
  };

  const formSubmitWithPublish = (publish: boolean) => {
    return [
      async (
        values: DesignCollectionDetailFormValueType,
        formikHelpers: FormikHelpers<DesignCollectionDetailFormValueType>
      ) => {
        return handleSaveAndPublish(values, formikHelpers, {
          publish,
        });
      },
      {
        translationPrefix: [
          "libraries.design_collections.detail",
          publish ? "publish" : "save",
        ].join("."),
        onError: () => {
          // refetch entity from server
          queryClient.invalidateQueries(
            queryKeyForDesignCollection(
              designCollection.id!,
              ALL_PUBLISHED_STATES
            )
          );
        },
      },
    ] as const;
  };

  const onSave = useFormSubmit(...formSubmitWithPublish(false));
  const onPublish = useFormSubmit(...formSubmitWithPublish(true));

  const initialValues = designCollectionToInitialValues(designCollection);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validateOnBlur={false}
      validateOnChange={false}
    >
      <DirtyFormPrompt>
        <DisplayDesignCollectionDetailForm
          designCollection={designCollection}
          refetch={refetch}
          referencesDrawer={referencesDrawer}
        />
      </DirtyFormPrompt>
    </Formik>
  );
};
